/**
 * Block 13 PR-G Phase 2 Step 2 — converts an integer amount to Russian words.
 *
 * Output convention: words only (no currency word appended). The contract
 * template combines digits + parenthetical words + currency word from
 * `template.meta.currency`. Example template fragment:
 *   "составляет 1 234 567 ({words from this function}) тенге"
 * Currency-aware composition is handled by Step 3 `contractTextRender.ts`.
 *
 * The `currency` parameter is accepted per handoff signature и зарезервирован
 * под gender agreement последней цифры. KZT/RUB/UZS — все masculine, поэтому
 * outputs идентичны для трёх currency values при том же `n`. Reserved for
 * future feminine currencies (e.g., «гривна» → «одна гривна»).
 *
 * Design choice — custom implementation (no npm dep):
 *   - kk/uz custom anyway (no npm packages exist for those locales);
 *     custom ru keeps the algorithm shape consistent across all 3 modules.
 *   - Standard Russian amount-in-words algorithm is well-defined (~150 LOC).
 *   - npm packages like num-rus default to ruble/kopeck and don't cleanly
 *     accept arbitrary currency tokens. Multi-currency contract use-case
 *     needs custom logic regardless of which lib we pick.
 *   - Zero-dep matches project preference (Python stdlib для scripts;
 *     `extract-contract-templates.py` outlined that approach в ADR-0025 §Q1).
 *
 * Reference:
 *   - ADR-0025 §Q5 «Сумма прописью client-side»
 *   - Block_13_PR_G_phase_0_planning.md Phase 2 Step 2
 */

export type AmountCurrency = 'KZT' | 'RUB' | 'UZS';

const UNITS_MASCULINE = [
  '',
  'один',
  'два',
  'три',
  'четыре',
  'пять',
  'шесть',
  'семь',
  'восемь',
  'девять',
] as const;

const UNITS_FEMININE = [
  '',
  'одна',
  'две',
  'три',
  'четыре',
  'пять',
  'шесть',
  'семь',
  'восемь',
  'девять',
] as const;

const TEENS = [
  'десять',
  'одиннадцать',
  'двенадцать',
  'тринадцать',
  'четырнадцать',
  'пятнадцать',
  'шестнадцать',
  'семнадцать',
  'восемнадцать',
  'девятнадцать',
] as const;

const TENS = [
  '',
  '',
  'двадцать',
  'тридцать',
  'сорок',
  'пятьдесят',
  'шестьдесят',
  'семьдесят',
  'восемьдесят',
  'девяносто',
] as const;

const HUNDREDS = [
  '',
  'сто',
  'двести',
  'триста',
  'четыреста',
  'пятьсот',
  'шестьсот',
  'семьсот',
  'восемьсот',
  'девятьсот',
] as const;

/**
 * Russian plural form picker. `forms` is `[singular, paucal, plural]`:
 *   - singular: 1, 21, 31, ... 101, 121 (last digit 1, except 11)
 *   - paucal:   2-4, 22-24, ... (last digit 2-4, except 12-14)
 *   - plural:   0, 5-20, 25-30, ... 100, 105-120, ...
 */
function pickPluralForm(n: number, forms: readonly [string, string, string]): string {
  const abs = Math.abs(n);
  const last2 = abs % 100;
  const last1 = abs % 10;
  if (last2 >= 11 && last2 <= 14) return forms[2];
  if (last1 === 1) return forms[0];
  if (last1 >= 2 && last1 <= 4) return forms[1];
  return forms[2];
}

/**
 * Convert 0-999 to Russian words. Words for 1 and 2 depend on grammatical
 * gender of the noun that follows (thousand=feminine, million/billion=masculine,
 * units of currency=masculine in our 3 supported currencies).
 */
function convertGroup(n: number, feminine: boolean): string[] {
  if (n === 0) return [];
  const result: string[] = [];
  const h = Math.floor(n / 100);
  const t = Math.floor((n % 100) / 10);
  const u = n % 10;
  // Indices гарантированно в диапазоне (h: 0-9 by /100, t: 0-9 by /10, u: 0-9
  // by %10). Non-null assertions safe — `noUncheckedIndexedAccess` strict mode.
  if (h > 0) result.push(HUNDREDS[h]!);
  if (t === 1) {
    result.push(TEENS[u]!);
  } else {
    if (t > 1) result.push(TENS[t]!);
    if (u > 0) {
      result.push(feminine ? UNITS_FEMININE[u]! : UNITS_MASCULINE[u]!);
    }
  }
  return result;
}

const THOUSAND_FORMS = ['тысяча', 'тысячи', 'тысяч'] as const;
const MILLION_FORMS = ['миллион', 'миллиона', 'миллионов'] as const;
const BILLION_FORMS = ['миллиард', 'миллиарда', 'миллиардов'] as const;

export function amountInWordsRu(n: number, _currency: AmountCurrency): string {
  if (!Number.isFinite(n) || !Number.isInteger(n)) {
    throw new Error(`amountInWordsRu: expected finite integer, got ${n}`);
  }
  if (n < 0) {
    return `минус ${amountInWordsRu(-n, _currency)}`;
  }
  if (n === 0) {
    return 'ноль';
  }

  const billions = Math.floor(n / 1_000_000_000);
  const millions = Math.floor((n % 1_000_000_000) / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1000);
  const units = n % 1000;

  const parts: string[] = [];

  if (billions > 0) {
    parts.push(...convertGroup(billions, false));
    parts.push(pickPluralForm(billions, BILLION_FORMS));
  }
  if (millions > 0) {
    parts.push(...convertGroup(millions, false));
    parts.push(pickPluralForm(millions, MILLION_FORMS));
  }
  if (thousands > 0) {
    parts.push(...convertGroup(thousands, true));
    parts.push(pickPluralForm(thousands, THOUSAND_FORMS));
  }
  if (units > 0) {
    parts.push(...convertGroup(units, false));
  }

  return parts.join(' ');
}
